import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EVENT_TYPE_CONFIG, CalendarEventType } from "@/hooks/useCalendarEvents";
import { cn } from "@/lib/utils";

interface CalendarSidebarProps {
  currentMonth: Date;
  onMonthChange: (d: Date) => void;
  onDayClick: (d: Date) => void;
  selectedDate: Date;
  layers: Record<string, boolean>;
  onLayerToggle: (key: string, checked: boolean) => void;
}

const LAYER_KEYS: CalendarEventType[] = [
  "milestone",
  "wo_schedule",
  "draw_deadline",
  "inspection",
  "meeting",
  "personal",
  "system_alert",
  "payment_due",
  "google_calendar",
];

const STORAGE_KEY = "commandx_cal_layers";

export function getInitialLayers(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  const defaults: Record<string, boolean> = {};
  LAYER_KEYS.forEach((k) => (defaults[k] = true));
  return defaults;
}

export function saveLayers(layers: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(layers));
}

export const CalendarSidebar = ({
  currentMonth,
  onMonthChange,
  onDayClick,
  selectedDate,
  layers,
  onLayerToggle,
}: CalendarSidebarProps) => {
  // Mini month navigator
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let i = 1; i <= totalDays; i++) days.push(i);

  const prevMonth = () => onMonthChange(new Date(year, month - 1, 1));
  const nextMonth = () => onMonthChange(new Date(year, month + 1, 1));

  const monthLabel = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="w-[200px] shrink-0 border-r bg-card p-3 space-y-4 overflow-y-auto">
      {/* Mini month nav */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={prevMonth}>
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <span className="text-xs font-semibold">{monthLabel}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={nextMonth}>
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-0 text-center">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i} className="text-[9px] text-muted-foreground font-medium">
              {d}
            </span>
          ))}
          {days.map((d, i) => {
            if (d === null) return <span key={`pad-${i}`} />;
            const date = new Date(year, month, d);
            const isToday =
              date.getFullYear() === today.getFullYear() &&
              date.getMonth() === today.getMonth() &&
              date.getDate() === today.getDate();
            const isSelected =
              date.getFullYear() === selectedDate.getFullYear() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getDate() === selectedDate.getDate();

            return (
              <button
                key={d}
                onClick={() => onDayClick(date)}
                className={cn(
                  "text-[10px] h-5 w-5 rounded-full flex items-center justify-center transition-colors mx-auto",
                  isToday && "bg-primary text-primary-foreground font-bold",
                  isSelected && !isToday && "bg-accent text-accent-foreground",
                  !isToday && !isSelected && "hover:bg-muted"
                )}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      {/* Layer toggles */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Calendar Layers
        </p>
        <div className="space-y-1.5">
          {LAYER_KEYS.map((key) => {
            const cfg = EVENT_TYPE_CONFIG[key];
            return (
              <label key={key} className="flex items-center gap-2 cursor-pointer text-xs">
                <Checkbox
                  checked={layers[key] !== false}
                  onCheckedChange={(c) => onLayerToggle(key, !!c)}
                  className="h-3.5 w-3.5"
                />
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: cfg.color }}
                />
                <span className="truncate">{cfg.label}</span>
              </label>
            );
          })}
        </div>
      </div>

    </div>
  );
};
